import { SENSITIVE_DATA_CATEGORIES, type SensitiveDataCategory } from './sensitiveDataCategories'

export type VisibleSensitiveData = ReadonlySet<SensitiveDataCategory>

export const EVERY_SENSITIVE_DATA_CATEGORY: VisibleSensitiveData = new Set(SENSITIVE_DATA_CATEGORIES)
export const NO_SENSITIVE_DATA_CATEGORY: VisibleSensitiveData = new Set()

// Shown in text in place of a value the server hid from its reader.
export const RESTRICTED_VALUE = 'Restricted'

export const hiddenCategories = (visible: VisibleSensitiveData): SensitiveDataCategory[] =>
  SENSITIVE_DATA_CATEGORIES.filter((category) => !visible.has(category))

export const seesEverySensitiveData = (visible: VisibleSensitiveData): boolean =>
  SENSITIVE_DATA_CATEGORIES.every((category) => visible.has(category))

const knownCategory = (category: string): SensitiveDataCategory | undefined =>
  SENSITIVE_DATA_CATEGORIES.find((known) => known === category)

// A category this package does not know yet always hides, named as the first known one, since readers accept only known names.
export const categoryHiding = (
  value: unknown,
  categories: readonly string[],
  visible: VisibleSensitiveData
): SensitiveDataCategory | undefined => {
  if (value == null) return undefined
  const hiding = categories.find((category) => {
    const known = knownCategory(category)
    return known === undefined || !visible.has(known)
  })
  return hiding === undefined ? undefined : (knownCategory(hiding) ?? SENSITIVE_DATA_CATEGORIES[0])
}

/** Hides each marked property the reader may not see: it becomes null and `redacted` names its category. */
export const hideProperties = (
  object: Record<string, unknown>,
  marked: Readonly<Record<string, readonly string[]>>,
  visible: VisibleSensitiveData
): Record<string, unknown> => {
  const redacted = Object.fromEntries(
    Object.entries(marked).flatMap(([key, categories]) => {
      const category = categoryHiding(object[key], categories, visible)
      return category === undefined ? [] : [[key, category]]
    })
  )
  if (Object.keys(redacted).length === 0) return object
  return { ...object, ...Object.fromEntries(Object.keys(redacted).map((key) => [key, null])), redacted }
}

export type RedactableKey<R> = R extends { redacted?: infer Redacted } ? keyof NonNullable<Redacted> : never

/** Returns true if the server hid `key` of this record from the caller, so its null means "hidden", not "empty" */
export const isRedactedProperty = <R extends object>(record: R, key: RedactableKey<R>): boolean =>
  Object.hasOwn(record, 'redacted') &&
  'redacted' in record &&
  typeof record.redacted === 'object' &&
  record.redacted !== null &&
  Object.hasOwn(record.redacted, key)
